import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, Download, Loader2, AlertCircle, CheckCircle, BookOpen, GraduationCap, Globe } from 'lucide-react';
import { useCredits } from '../contexts/CreditContext';
import { getApiUrl } from '../config';

function LMSCourseCreator() {
  const { updateCreditsFromResponse } = useCredits();
  const [files, setFiles] = useState([]);
  const [courseTitle, setCourseTitle] = useState('');
  const [courseDescription, setCourseDescription] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
    setError('');
    setSuccess('');
    setDownloadUrl('');
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!files.length) {
      setError('Please upload at least one file.');
      return;
    }
    if (!courseTitle.trim()) {
      setError('Please enter a course title.');
      return;
    }
    
    setIsGenerating(true);
    setError('');
    setSuccess('');
    setDownloadUrl('');
    
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));
      formData.append('course_title', courseTitle);
      formData.append('course_description', courseDescription);
      
      const response = await axios.post(getApiUrl('/generate_scorm'), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });
      
      setDownloadUrl(response.data.downloadUrl);
      setSuccess('SCORM package generated successfully! You can now download and upload it to any LMS.');
      // Update credits from response
      updateCreditsFromResponse(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate SCORM package. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            LMS Course Creator
          </h2>
          <p className="text-gray-600 text-lg">
            Upload your source materials and generate SCORM-compliant course packages 
            that can be uploaded to any Learning Management System.
          </p>
        </div>

        <form onSubmit={handleGenerate} className="space-y-6">
          {/* Course Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Title *
              </label>
              <input
                type="text"
                value={courseTitle}
                onChange={(e) => setCourseTitle(e.target.value)}
                placeholder="Enter course title..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Description
              </label>
              <textarea
                value={courseDescription}
                onChange={(e) => setCourseDescription(e.target.value)}
                placeholder="Enter course description..."
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Source Materials *
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                accept=".pdf,.docx,.pptx,.txt,.html,.htm"
                className="hidden"
                id="lms-file-upload"
              />
              <label
                htmlFor="lms-file-upload"
                className="cursor-pointer inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Upload className="w-4 h-4 mr-2" />
                Choose Files
              </label>
              <p className="mt-2 text-sm text-gray-500">
                Supported formats: PDF, DOCX, PPTX, TXT, HTML
              </p>
              {files.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700">Selected files:</p>
                  <ul className="mt-2 text-sm text-gray-600">
                    {files.map((file, index) => (
                      <li key={index} className="flex items-center justify-between">
                        <span>{file.name}</span>
                        <span className="text-xs text-gray-500">
                          ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Generate Button */}
          <div className="flex justify-center">
            <button
              type="submit"
              disabled={isGenerating || files.length === 0 || !courseTitle.trim()}
              className={`px-8 py-3 text-lg font-medium rounded-md text-white ${
                isGenerating || files.length === 0 || !courseTitle.trim()
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
              }`}
            >
              {isGenerating ? (
                <div className="flex items-center">
                  <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                  Generating SCORM Package...
                </div>
              ) : (
                <div className="flex items-center">
                  <GraduationCap className="w-5 h-5 mr-2" />
                  Generate SCORM Package
                </div>
              )}
            </button>
          </div>
        </form>

        {/* Error Message */}
        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <CheckCircle className="h-5 w-5 text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700">{success}</p>
              </div>
            </div>
          </div>
        )}

        {/* Download Section */}
        {downloadUrl && (
          <div className="mt-6 text-center">
            <a
              href={downloadUrl}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              download
            >
              <Download className="mr-2 h-5 w-5" />
              Download SCORM Package (ZIP)
            </a>
          </div>
        )}

        {/* Features */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="bg-blue-100 rounded-full w-12 h-12 mx-auto mb-4 flex items-center justify-center">
              <BookOpen className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">AI-Powered Content</h3>
            <p className="text-gray-600 text-sm">
              Automatically convert your source materials into structured learning content using advanced AI.
            </p>
          </div>

          <div className="text-center">
            <div className="bg-green-100 rounded-full p-3 w-12 h-12 mx-auto mb-4 flex items-center justify-center">
              <Globe className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">LMS Compatible</h3>
            <p className="text-gray-600 text-sm">
              Generate SCORM 1.2 compliant packages that work with any Learning Management System.
            </p>
          </div>

          <div className="text-center">
            <div className="bg-purple-100 rounded-full p-3 w-12 h-12 mx-auto mb-4 flex items-center justify-center">
              <GraduationCap className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Interactive Learning</h3>
            <p className="text-gray-600 text-sm">
              Create engaging courses with quizzes, assessments, and progress tracking capabilities.
            </p>
          </div>
        </div>

        {/* How it works */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">How it works</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-blue-800">
            <div className="flex items-start">
              <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</div>
              <div>Upload your source materials and provide course information (title, description).</div>
            </div>
            <div className="flex items-start">
              <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</div>
              <div>AI analyzes your content and creates structured learning modules with assessments.</div>
            </div>
            <div className="flex items-start">
              <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</div>
              <div>Download the SCORM package and upload it to any LMS platform for deployment.</div>
            </div>
          </div>
        </div>

        {/* LMS Compatibility Info */}
        <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">LMS Compatibility</h3>
          <p className="text-sm text-gray-700 mb-3">
            The generated SCORM packages are compatible with all major Learning Management Systems including:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-600">
            <div>• Canvas</div>
            <div>• Blackboard</div>
            <div>• Moodle</div>
            <div>• Cornerstone</div>
            <div>• Workday</div>
            <div>• SuccessFactors</div>
            <div>• Docebo</div>
            <div>• And many more...</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LMSCourseCreator; 